import { ProjectElementType, TextBlockType } from '../enum/EnumRepository';
import type {
  ImageMetadata,
  ProjectElementData,
  ProjectElementDataWithWork,
  ProjectElementDataWithDetail,
  ProjectElementDataWithDocument,
  ProjectElementDataWithText,
  ProjectElementDataWithFormation,
} from '../dto/EntityRepository';
import { convertS3UrlToCloudFrontUrl } from '../aws/awsHelper';
import { resolveFormationSlotImage } from './formationUtils';
import {
  convertToJsonMetadata,
  generateOptimizedUrl,
  isDeviceAvailable,
} from './findAdjustImg';

const IMAGE_RESOLVE_CONCURRENCY = 5;
const imageMetadataCache = new Map<string, ImageMetadata>();
const imageMetadataRequestCache = new Map<string, Promise<ImageMetadata>>();

export type PdfImageQuality = 'LOW' | 'MEDIUM' | 'HIGH';

const PDF_IMAGE_DEVICE_FALLBACK_PRIORITY: Record<
  PdfImageQuality,
  readonly string[]
> = {
  HIGH: ['LAPTOP', 'DESKTOP', 'origin'],
  MEDIUM: ['TABLET', 'LAPTOP', 'DESKTOP', 'origin'],
  LOW: ['MOBILE', 'TABLET', 'LAPTOP', 'origin'],
};

/** 같은 이미지의 meta.json은 최초 한 번만 요청하고 이후 Quality 변경 시 캐시를 사용 */
async function getImageMetadata(baseUrl: string): Promise<ImageMetadata> {
  const cached = imageMetadataCache.get(baseUrl);
  if (cached) return cached;

  const pending = imageMetadataRequestCache.get(baseUrl);
  if (pending) return pending;

  const request = (async () => {
    const jsonUrl = baseUrl.replace(
      /\/[^/]+\.(?:jpeg|jpg|png|gif|webp)$/i,
      `/meta.json`
    );
    const response = await fetch(jsonUrl, { cache: 'no-cache' });
    if (!response.ok) throw new Error('Failed to fetch metadata');
    const contentType = response.headers.get('content-type');
    if (!contentType?.includes('application/json')) {
      throw new Error('Response is not JSON');
    }
    const metadata = convertToJsonMetadata(await response.json());
    imageMetadataCache.set(baseUrl, metadata);
    return metadata;
  })();

  imageMetadataRequestCache.set(baseUrl, request);
  try {
    return await request;
  } finally {
    imageMetadataRequestCache.delete(baseUrl);
  }
}

/** 최대 concurrency개만 동시 실행, 나머지는 대기 */
async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> {
  const results: R[] = [];
  let index = 0;
  async function run(): Promise<void> {
    while (index < items.length) {
      const i = index++;
      results[i] = await fn(items[i]);
    }
  }
  const workers = Array.from({ length: Math.min(concurrency, items.length) }, () => run());
  await Promise.all(workers);
  return results;
}

/** GIF URL을 로드해 첫 프레임만 PNG 데이터 URL로 반환 (PDF는 GIF 미지원). 사용 후 img 참조 해제로 디코딩 비트맵 메모리 조기 해제 */
export function getGifFirstFrameDataUrl(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = document.createElement('img');
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context unavailable'));
          return;
        }
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL('image/png');
        img.src = '';
        img.onload = null;
        img.onerror = null;
        resolve(dataUrl);
      } catch (e) {
        img.src = '';
        img.onload = null;
        img.onerror = null;
        reject(e);
      }
    };
    img.onerror = () => {
      img.onload = null;
      img.onerror = null;
      reject(new Error('Failed to load GIF'));
    };
    img.src = url;
  });
}

/** 이미지 URL을 로드해 JPEG 데이터 URL로 반환 (PDF에서 WebP 미지원 시 사용). 사용 후 img 참조 해제로 디코딩 비트맵 메모리 조기 해제 */
function getImageAsJpegDataUrl(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = document.createElement('img');
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context unavailable'));
          return;
        }
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL('image/jpeg');
        img.src = '';
        img.onload = null;
        img.onerror = null;
        resolve(dataUrl);
      } catch (e) {
        img.src = '';
        img.onload = null;
        img.onerror = null;
        reject(e);
      }
    };
    img.onerror = () => {
      img.onload = null;
      img.onerror = null;
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });
}

export interface ResolvedProjectImageUrls {
  imageUrlMap: Record<string, string>;
  gifFirstFrameMap: Record<string, string>;
}

/** projectElementList에서 이미지 URL 수집 후 GIF는 첫 프레임 PNG, 나머지는 메타 기반 최적 URL로 해석 (PDF 생성 시 첫 렌더부터 사용 가능하도록) */
export async function resolveProjectImageUrls(
  projectElementList: ProjectElementData[],
  quality: PdfImageQuality = 'MEDIUM'
): Promise<ResolvedProjectImageUrls> {
  const imageUrlMap: Record<string, string> = {};
  const gifFirstFrameMap: Record<string, string> = {};

  if (!projectElementList?.length) {
    return { imageUrlMap, gifFirstFrameMap };
  }

  const urls = new Set<string>();
  const inlineImageUrlMap: Record<string, string> = {};

  const collectImageUrl = (originUrl: string | undefined) => {
    if (!originUrl) return;
    if (originUrl.startsWith('data:')) {
      inlineImageUrlMap[originUrl] = originUrl;
      return;
    }
    const baseUrl = convertS3UrlToCloudFrontUrl(originUrl);
    if (baseUrl && baseUrl.startsWith('http')) urls.add(baseUrl);
  };

  projectElementList.forEach((element) => {
    if (element.projectElementType === ProjectElementType.WORK) {
      const work = (element as ProjectElementDataWithWork).work;
      collectImageUrl(work?.uploadFile?.originUrl);
    }
    if (element.projectElementType === ProjectElementType.DETAIL) {
      const workDetail = (element as ProjectElementDataWithDetail).workDetail;
      collectImageUrl(workDetail?.uploadFile?.originUrl);
    }
    if (element.projectElementType === ProjectElementType.DOCUMENT) {
      const document = (element as ProjectElementDataWithDocument).document;
      collectImageUrl(document?.uploadFile?.originUrl);
    }
    if (element.projectElementType === ProjectElementType.TEXT) {
      const selectedTextBlocks = (element as ProjectElementDataWithText).selectedTextBlocks ?? [];
      selectedTextBlocks.forEach((block) => {
        if (block.type !== TextBlockType.IMAGE) return;
        collectImageUrl(block.uploadFile?.originUrl);
      });
    }
    if (element.projectElementType === ProjectElementType.FORMATION) {
      const formation = (element as ProjectElementDataWithFormation).formation;
      formation?.items?.forEach((item) => {
        const resolved = resolveFormationSlotImage(item);
        collectImageUrl(resolved?.originUrl);
      });
    }
  });

  const urlList = Array.from(urls);
  const gifUrls = urlList.filter((u) => /\.gif$/i.test(u));
  const nonGifUrls = urlList.filter((u) => !/\.gif$/i.test(u));

  const [gifEntries, entries] = await Promise.all([
    mapWithConcurrency(gifUrls, IMAGE_RESOLVE_CONCURRENCY, async (baseUrl) => {
      try {
        const dataUrl = await getGifFirstFrameDataUrl(baseUrl);
        return [baseUrl, dataUrl] as const;
      } catch {
        return [baseUrl, baseUrl] as const;
      }
    }),
    mapWithConcurrency(nonGifUrls, IMAGE_RESOLVE_CONCURRENCY, async (baseUrl) => {
      if (/\.webp$/i.test(baseUrl)) {
        try {
          const jpegDataUrl = await getImageAsJpegDataUrl(baseUrl);
          return [baseUrl, jpegDataUrl] as const;
        } catch {
          return [baseUrl, baseUrl] as const;
        }
      }
      let finalUrl = baseUrl;
      try {
        const metadata = await getImageMetadata(baseUrl);
        for (const deviceType of PDF_IMAGE_DEVICE_FALLBACK_PRIORITY[quality]) {
          if (!isDeviceAvailable(metadata, deviceType)) continue;
          finalUrl = generateOptimizedUrl(baseUrl, deviceType, metadata);
          break;
        }
      } catch {
        // ignore, use baseUrl
      }
      if (/\.webp$/i.test(finalUrl)) {
        try {
          const jpegDataUrl = await getImageAsJpegDataUrl(finalUrl);
          return [baseUrl, jpegDataUrl] as const;
        } catch {
          return [baseUrl, finalUrl] as const;
        }
      }
      return [baseUrl, finalUrl] as const;
    }),
  ]);

  gifEntries.forEach(([baseUrl, dataUrl]) => {
    gifFirstFrameMap[baseUrl] = dataUrl;
  });
  entries.forEach(([baseUrl, finalUrl]) => {
    imageUrlMap[baseUrl] = finalUrl;
  });
  Object.assign(imageUrlMap, inlineImageUrlMap);

  return { imageUrlMap, gifFirstFrameMap };
}
